'use client';

import { Fragment, useMemo, type ReactNode } from 'react';
import {
  LocaleContext,
  RenderOneBlock,
  ThemeContext,
  assignBlockIds,
  isVisible,
  useCurrentUser,
  type BlockVisibilityProps,
  type CurrentUser,
} from '@akademi/site-builder-runtime';
import { BLOCK_GROUPS } from '@akademi/site-builder-blocks';
import { COMMERCE_BLOCK_GROUPS } from '@commerce/site-builder-blocks';
import type { ComposedTreeNode } from '@/src/lib/server/sitebuilder';

/**
 * Walks the composed tree the backend's `render/route` and `render/shell`
 * return.
 *
 * <ul>
 *   <li>`Section` — a structural wrapper: a `div` plus its `wrapperClass`.</li>
 *   <li>`Fragment` — children with no wrapper of their own.</li>
 *   <li>anything else — a block type id, resolved through the host resolver.</li>
 * </ul>
 */

const STRUCTURAL = new Set(['Section', 'Fragment']);

function isNestedBlock(value: unknown): value is { type: string; props: Record<string, unknown> } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const block = value as { type?: unknown; props?: unknown };
  return (
    typeof block.type === 'string' &&
    block.type.length > 0 &&
    !!block.props &&
    typeof block.props === 'object' &&
    !Array.isArray(block.props)
  );
}

function collectNested(
  props: Record<string, unknown>,
  out: Array<{ type: string; props: Record<string, unknown> }>,
): void {
  for (const value of Object.values(props)) {
    for (const item of Array.isArray(value) ? value : [value]) {
      if (isNestedBlock(item)) {
        out.push({ type: item.type, props: item.props });
        collectNested(item.props, out);
      }
    }
  }
}

function collectBlocks(
  node: ComposedTreeNode | undefined,
  out: Array<{ type: string; props: Record<string, unknown> }>,
): void {
  if (!node || typeof node !== 'object') return;
  const type = typeof node.type === 'string' ? node.type : undefined;
  if (type && !STRUCTURAL.has(type) && node.props && typeof node.props === 'object') {
    const props = node.props as Record<string, unknown>;
    if (node.id && props._nodeId == null) props._nodeId = node.id;
    out.push({ type, props });
    collectNested(props, out);
  }
  node.children?.forEach((child) => collectBlocks(child, out));
}

/**
 * Stamp every block instance with the identity attributes the runtime reads
 * back out (`_gbtId`, `_gbtGroup`). Memoised on the raw tree so it runs once
 * per payload, not once per render.
 */
function decorateTreeWithIds(tree: ComposedTreeNode): ComposedTreeNode {
  const copy: ComposedTreeNode =
    typeof structuredClone === 'function'
      ? structuredClone(tree)
      : (JSON.parse(JSON.stringify(tree)) as ComposedTreeNode);
  const blocks: Array<{ type: string; props: Record<string, unknown> }> = [];
  collectBlocks(copy, blocks);
  const ids = assignBlockIds(blocks);
  blocks.forEach((block, index) => {
    block.props._gbtId = ids[index];
    const group = BLOCK_GROUPS[block.type] ?? COMMERCE_BLOCK_GROUPS[block.type];
    if (group) block.props._gbtGroup = group;
  });
  return copy;
}

export function ComposedTreeRenderer({
  tree,
  themeId,
  locale,
  fallbackLocale,
}: {
  tree: ComposedTreeNode;
  themeId?: string | null;
  locale: string;
  fallbackLocale: string;
}) {
  const decorated = useMemo(() => decorateTreeWithIds(tree), [tree]);
  return (
    <ThemeContext.Provider value={themeId ?? null}>
      <LocaleContext.Provider value={{ locale, fallback: fallbackLocale }}>
        <RenderNode node={decorated} />
      </LocaleContext.Provider>
    </ThemeContext.Provider>
  );
}

function renderChildren(children: ComposedTreeNode[] | undefined): ReactNode {
  if (!children?.length) return null;
  return children.map((child, index) => (
    <RenderNode key={child.id ?? `${child.type ?? 'node'}-${index}`} node={child} />
  ));
}

function RenderNode({ node }: { node: ComposedTreeNode }) {
  if (!node || typeof node !== 'object') return null;
  const type = typeof node.type === 'string' ? node.type : undefined;

  if (type === 'Fragment') {
    if (node.catchall) {
      return (
        <div className="sb-catchall" data-label={node.label ?? undefined}>
          {renderChildren(node.children)}
        </div>
      );
    }
    return <Fragment>{renderChildren(node.children)}</Fragment>;
  }
  if (type === 'Section') {
    return <div className={node.wrapperClass}>{renderChildren(node.children)}</div>;
  }
  return <BlockLeaf node={node} />;
}

const LEAF_STRUCTURAL_KEYS = new Set([
  'type',
  'id',
  'children',
  'catchall',
  'wrapperClass',
  'label',
  'props',
]);

function BlockLeaf({ node }: { node: ComposedTreeNode }) {
  const currentUser = useCurrentUser<CurrentUser>();
  // Entity-derived nodes carry their props flat on the node rather than nested
  // under `props`; falling back keeps those from mounting with an empty set.
  const props =
    node.props ??
    Object.fromEntries(Object.entries(node).filter(([key]) => !LEAF_STRUCTURAL_KEYS.has(key)));

  // The per-instance "Visible to" rule an operator authored in the designer.
  // `RenderOneBlock` only strips the key — it assumes its caller gated — so
  // skipping this check here makes every authored visibility rule a no-op.
  const visibility = props['_visibility'];
  if (
    visibility &&
    typeof visibility === 'object' &&
    !Array.isArray(visibility) &&
    !isVisible(visibility as BlockVisibilityProps, currentUser)
  ) {
    return null;
  }

  return <RenderOneBlock instance={{ type: node.type, props }} />;
}
